import type { ChangeEvent, SubmitEvent } from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { ANALYTICS_EVENTS, ANALYTICS_JOURNEY_CHANNELS, trackAnalyticsEvent, trackNavigationEvent } from '@/services/analytics'
import { isQuickSearchQueryLongEnough, isValidQuickSearchQuery, MAX_QUICK_SEARCH_QUERY_LENGTH } from '@/utils/quickSearch'

export function useNavigationHeaderSearch(currentSeason: string | null) {
  const navigate = useNavigate()
  const [inputValue, setInputValue] = useState('')
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  const shouldOpenSearch = isQuickSearchQueryLongEnough(inputValue) && isSearchOpen && !!currentSeason

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!currentSeason)
      return

    const nextValue = String(event.target.value ?? '').slice(0, MAX_QUICK_SEARCH_QUERY_LENGTH)
    const hasMinimumQueryLength = isQuickSearchQueryLongEnough(nextValue)
    const shouldOpenFromTyping = hasMinimumQueryLength && !isSearchOpen

    if (shouldOpenFromTyping) {
      trackAnalyticsEvent(ANALYTICS_EVENTS.QUICK_SEARCH_OPENED, {
        journey_channel: ANALYTICS_JOURNEY_CHANNELS.QUICK_SEARCH,
        source_location: 'navigation_header',
        open_trigger: 'typing',
        season: currentSeason,
      })
    }

    setInputValue(nextValue)
    setIsSearchOpen(hasMinimumQueryLength)
  }

  const onInputFocus = () => {
    if (isQuickSearchQueryLongEnough(inputValue) && currentSeason) {
      setIsSearchOpen(true)
    }
  }

  const onSearchClose = () => {
    setIsSearchOpen(false)
    setInputValue(value => isQuickSearchQueryLongEnough(value) ? '' : value)
  }

  const onPanelClose = () => {
    setIsSearchOpen(false)
    setInputValue('')
  }

  const onSearchSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    const normalizedInput = inputValue.trim()

    if (!isValidQuickSearchQuery(normalizedInput) || !currentSeason)
      return

    trackNavigationEvent(ANALYTICS_EVENTS.QUICK_SEARCH_QUERY_SUBMITTED, 'quick_search', {
      journey_channel: ANALYTICS_JOURNEY_CHANNELS.QUICK_SEARCH,
      source_location: 'navigation_header',
      season: currentSeason,
      query: normalizedInput,
      query_length: normalizedInput.length,
    })

    void navigate(`/${currentSeason}/search?query=${encodeURIComponent(normalizedInput)}`)
    setIsSearchOpen(false)
    setInputValue('')
  }

  return {
    inputValue,
    shouldOpenSearch,
    onInputChange,
    onInputFocus,
    onSearchClose,
    onPanelClose,
    onSearchSubmit,
  }
}
